import { Injectable, Type } from '@angular/core';
import {
	BehaviorSubject,
	combineLatest,
	firstValueFrom,
	Observable,
	Subscription,
} from 'rxjs';
import { LeapLoginComponent } from '../auth/leap-login/login.component';
import { LeapConfirmationComponent } from './onboarding/confirmation/confirmation.component';
import { LeapWelcomeComponent } from './onboarding/welcome/welcome.component';
import {
	LeapApiService,
	LeapCheckMatterResponse,
	LeapMatterResponse,
	LeapOnboardingStatusResponse,
	PersonSyncStatus,
	UserSyncData,
} from './leap-api.service';
import { AuthDispatch } from '@state/auth/auth.dispatch';
import { Actions, Select, Store } from '@ngxs/store';
import { NavigationService } from '@services/nav/nav.service';
import LogRocket from 'logrocket';
import { filter, take } from 'rxjs/operators';
import {
	GoToUrlMessageData,
	LeapCrossWindowMessage,
	LeapWindowAction,
	LeapWindowType,
} from './state/leap.model';
import { LeapDispatch } from './state/leap.dispatch';
import { LeapState } from './state/leap.state';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { LeapSdkService } from './leap-sdk.service';
import { MatDialog } from '@angular/material/dialog';
import {
	ResolveLawyerClerkMismatchComponent,
	ResolveMismatchOption,
} from './modals/resolve-lawyer-clerk-mismatch/resolve-lawyer-clerk-mismatch.component';
import { CannotAddLawyerClerkToMatterComponent } from './modals/cannot-add-lawyer-clerk-to-matter/cannot-add-lawyer-clerk-to-matter.component';
import { LawyerMismatchNotificationComponent } from './modals/lawyer-mismatch-notification/lawyer-mismatch-notification.component';
import { BillingSetupComponent } from './billing-setup/billing-setup.component';
import { BusinessState } from '@state/business';
import { BillingBusinessTypesState } from '@state/billing-business';
import { MatterFileType } from '@models/matter';
import { VideoIntroComponent } from './onboarding/video-intro/video-intro.component';
import { UpdateLeapReminderComponent } from './onboarding/update-leap-reminder/update-leap-reminder.component';
import { ILeapOnboardingService } from './leap-onboarding.models';
import { Logger } from '@core/logger';
import {
	GetMattersWithWarning,
	SetMattersWithWarning,
} from 'src/app/shared/core/util/local-storage';
import { RejectImportingMatterComponent } from './modals/reject-importing-matter/reject-importing-matter.component';
import { WarnBeforeImportingMatterComponent } from './modals/warn-before-importing-matter/warn-before-importing-matter.component';
import { YesOrNo } from '@core/types';
import { InvalidMatterTypeComponent } from './modals/invalid-matter-type/invalid-matter-type.component';
import { AuthState } from 'src/app/shared/state/auth';

export type LeapOnboardingScreen = {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	component: Type<any>;
	panelClass?: string | string[];
};

export type LeapOnboardingScreens = Record<
	'videoIntro' | 'welcome' | 'updateLeapReminder' | 'login' | 'confirmation',
	LeapOnboardingScreen
>;

@Injectable({
	providedIn: 'root',
})
export class LeapOnboardingService implements ILeapOnboardingService {
	@Select(LeapState.message) message$: Observable<LeapCrossWindowMessage>;
	@SelectSnapshot(LeapState.message)
	private messageSnapshot: LeapCrossWindowMessage;

	public leapOnboardingScreens: LeapOnboardingScreens = {
		videoIntro: {
			component: VideoIntroComponent,
			panelClass: ['max-w-[90vw]', 'w-[90vw]', '2xl:w-3/4', '5xl:w-3/5'],
		},
		welcome: {
			component: LeapWelcomeComponent,
			panelClass: ['max-w-[90vw]', 'w-[90vw]', '2xl:w-3/4', '5xl:w-3/5'],
		},
		updateLeapReminder: {
			component: UpdateLeapReminderComponent,
			panelClass: ['w-1/2', '2xl:w-2/5'],
		},
		login: { component: LeapLoginComponent },
		confirmation: {
			component: LeapConfirmationComponent,
			panelClass: ['w-3/4', '2xl:w-2/3', '4xl:w-1/2', '5xl:w-2/5'],
		},
	};

	public showLoadingMessage$ = new BehaviorSubject('');
	public showLoadingIcon$ = new BehaviorSubject(true);
	public currentOnboardingStep$: BehaviorSubject<LeapOnboardingScreen> =
		new BehaviorSubject(this.leapOnboardingScreens.videoIntro);
	public isLoading$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	public userAccountBelongsToDifferentBusinessError$ = new BehaviorSubject({
		hasError: false,
		invalidEmail: '',
	});

	private customTokenHandlerSub: Subscription;
	private userId: string;
	private firmId: string;
	private redirectTo: string;
	private matterId: string;
	public onboardingStatusResponse: LeapOnboardingStatusResponse;
	private requestId: string;

	constructor(
		private leapApiService: LeapApiService,
		private authDispatch: AuthDispatch,
		private actions$: Actions,
		private navService: NavigationService,
		private leapDispatch: LeapDispatch,
		private leapSDKService: LeapSdkService,
		private dialog: MatDialog,
		private store: Store,
	) {}

	public async init(
		redirectTo: string,
		requestId: string,
		onboardingStatusResponse: LeapOnboardingStatusResponse,
	) {
		this.redirectTo = redirectTo;
		this.requestId = requestId;
		this.onboardingStatusResponse = onboardingStatusResponse;

		this.showLoadingMessage$.next('Authenticating with LEAP...');
		const userInfo = await this.leapSDKService.getUserInfo();
		this.userId = userInfo.userId;
		this.firmId = userInfo.firmId;
	}

	goToVideoIntroScreen() {
		this.currentOnboardingStep$.next(this.leapOnboardingScreens.videoIntro);
		this.isLoading$.next(false);
	}

	goToWelcomeScreen() {
		this.currentOnboardingStep$.next(this.leapOnboardingScreens.welcome);
		this.isLoading$.next(false);
	}

	goToUpdateLeapReminderScreen() {
		this.currentOnboardingStep$.next(
			this.leapOnboardingScreens.updateLeapReminder,
		);
		this.isLoading$.next(false);
	}

	goToLoginScreen() {
		this.currentOnboardingStep$.next(this.leapOnboardingScreens.login);
		this.isLoading$.next(false);
	}

	goToConfirmationScreen() {
		this.currentOnboardingStep$.next(this.leapOnboardingScreens.confirmation);
		this.isLoading$.next(false);
	}
	async connect(email?: string, businessId?: string) {
		this.isLoading$.next(true);

		const userToken = await this.leapApiService
			.getSignInToken(
				this.userId,
				this.firmId,

				// Get a refreshed token in case the user was inactive since we got the last token
				await this.leapSDKService.getAccessToken(),
				email,
				businessId,
			)
			.toPromise();

		await this.subscribeToSetUser();
		this.showLoadingMessage$.next('Signing into Closer...');
		this.authDispatch.SignInWithCustomToken(userToken);
	}

	async personSyncState(
		syncState: PersonSyncStatus,
		matterImported: boolean,
		hasMortgages: boolean,
		lawyerUserSyncData?: UserSyncData,
		clerkUserSyncData?: UserSyncData,
	): Promise<boolean> {
		// if the value is Same or NotApplicable, we do nothing.
		if (
			syncState === PersonSyncStatus.Same ||
			syncState === PersonSyncStatus.NotApplicable
		) {
			return false;
		}

		const lawyerNames = {
			leapLawyerName: lawyerUserSyncData?.leapName,
			closerLawyerName: lawyerUserSyncData?.closerName,
		};
		const clerkNames = {
			leapClerkName: clerkUserSyncData?.leapName,
			closerClerkName: clerkUserSyncData?.closerName,
		};

		let dialogType: Type<unknown>;
		let data = {};
		let disableClose = false;
		if (hasMortgages) {
			dialogType = LawyerMismatchNotificationComponent;
			data = lawyerNames;
		} else if (
			syncState === PersonSyncStatus.UserNotInLeap ||
			syncState === PersonSyncStatus.UserNotOnboarded
		) {
			dialogType = CannotAddLawyerClerkToMatterComponent;
			data = {
				leapLawyerName: lawyerNames?.leapLawyerName,
				leapClerkName: clerkNames?.leapClerkName,
			};
		} else if (syncState === PersonSyncStatus.Different && matterImported) {
			dialogType = ResolveLawyerClerkMismatchComponent;
			data = {
				mismatchedLawyers: lawyerNames,
				mismatchedClerks: clerkNames,
			};
			disableClose = true;
		} else {
			return false;
		}

		const dialogRef = this.dialog.open(dialogType, {
			data,
			disableClose,
		});

		const dialogResult: {
			resolveMismatchSelection: ResolveMismatchOption;
		} = await dialogRef.afterClosed().toPromise();

		return dialogResult
			? dialogResult.resolveMismatchSelection === ResolveMismatchOption.UPDATE
			: false;
	}

	async subscribeToSetUser() {
		const subscription = combineLatest([
			this.store.select(BusinessState.getLoaded),
			this.store.select(BillingBusinessTypesState.getLoaded),
			this.store.select(AuthState.isUserLoggedIn),
		])
			//check for all three to be true before proceeding
			.pipe(
				filter(
					([businessLoaded, billingLoaded, isUserLoggedIn]) =>
						businessLoaded && billingLoaded && isUserLoggedIn,
				),
				take(1),
			)
			.subscribe(async () => {
				await this.handleRedirection();
			});

		this.customTokenHandlerSub = subscription;
	}

	private async handleRedirection() {
		switch (this.redirectTo) {
			case 'overview':
				this.showLoadingMessage$.next('Navigating to dashboard...');
				console.log('In SetUser callback and now navigating to dashboard.');

				// Assume that only the host window would navigate to the dashboard
				this.leapDispatch.SetWindowType(LeapWindowType.HOST_WINDOW);

				this.navService.goToDashboard();
				break;
			case 'matterId': {
				this.showLoadingMessage$.next('Checking if matter exists...');

				await this.handleWarningBeforeImportUsingStorage();

				const matterImportedCheckResponse = await this.leapApiService
					.doesMatterExist(this.requestId)
					.toPromise();
				this.matterId = matterImportedCheckResponse.leapMatterId;

				if (matterImportedCheckResponse.invalidMatterType) {
					this.HandleInvalidMatterType(
						matterImportedCheckResponse.invalidMatterType,
					);

					return;
				}

				// If the matter is new (not imported yet) and billing is not set up and is outside
				// the trial period, then force user to set up billing and do not proceed with matter
				// creation
				if (
					!matterImportedCheckResponse.matterImported &&
					!this.store.selectSnapshot(
						BillingBusinessTypesState.getIsLeapFreeTrial,
					) &&
					!this.store.selectSnapshot(
						BusinessState.getHasActiveStripePaymentMethod,
					)
				) {
					this.dialog.open(BillingSetupComponent, {
						disableClose: true,
					});
					return;
				}

				await this.handleWarningBeforeImport(matterImportedCheckResponse);

				let updateLawyer = false;
				let updateClerk = false;

				if (
					matterImportedCheckResponse.lawyerState.personSyncState !==
						matterImportedCheckResponse.clerkState.personSyncState ||
					matterImportedCheckResponse.hasMortgages
				) {
					updateLawyer = await this.personSyncState(
						matterImportedCheckResponse.lawyerState.personSyncState,
						matterImportedCheckResponse.matterImported,
						matterImportedCheckResponse.hasMortgages,
						matterImportedCheckResponse.lawyerState,
						null,
					);

					updateClerk = await this.personSyncState(
						matterImportedCheckResponse.clerkState.personSyncState,
						matterImportedCheckResponse.matterImported,
						false,
						null,
						matterImportedCheckResponse.clerkState,
					);
				} else if (
					matterImportedCheckResponse.lawyerState.personSyncState !==
					PersonSyncStatus.Same
				) {
					updateLawyer = await this.personSyncState(
						matterImportedCheckResponse.lawyerState.personSyncState,
						matterImportedCheckResponse.matterImported,
						false,
						matterImportedCheckResponse.lawyerState,
						matterImportedCheckResponse.clerkState,
					);
					updateClerk = updateLawyer;
				}

				let matterResponse: LeapMatterResponse;
				if (matterImportedCheckResponse.matterImported) {
					this.showLoadingMessage$.next('Update matter from LEAP...');
					matterResponse = await this.leapApiService
						.updateMatter(this.matterId, updateLawyer, updateClerk)
						.toPromise();
				} else {
					this.showLoadingMessage$.next('Import matter from LEAP...');
					matterResponse = await this.leapApiService
						.importLeapMatter(this.matterId)
						.toPromise();
				}

				let localData: GoToUrlMessageData | null = null;
				if (
					this.messageSnapshot?.action ===
						LeapWindowAction.OPENING_MATTER_URL &&
					this.messageSnapshot?.data &&
					(this.messageSnapshot?.data as GoToUrlMessageData).relatedMatterId ===
						matterResponse.matterId
				) {
					localData = this.messageSnapshot.data as GoToUrlMessageData;
				}

				if (!localData) {
					LogRocket.log(
						'The item with the key in local storage is "null". ' +
							'This is fine if previous to this log there is no log that reads "The application should navigate to..."',
					);
				}

				if (localData) {
					this.navService.goToUrl(
						localData.url,
						localData.params || undefined,
						localData.extras || undefined,
					);
					this.leapDispatch.DeleteCrossWindowMessage();
					this.leapDispatch.SetWindowType(LeapWindowType.MATTER_WINDOW);

					LogRocket.log(
						'Going to a specific location of the matter.',
						localData.url,
					);
				} else {
					console.log(
						'In SetUser callback after "import-matter" and now navigating to matter details page.',
					);
					this.leapDispatch.SetWindowType(LeapWindowType.MATTER_WINDOW);

					if (
						!matterImportedCheckResponse.matterImported &&
						matterResponse.matterType !== MatterFileType.REFINANCE
					) {
						this.goToCreateMatter_APS(matterResponse.matterId);
					} else {
						this.redirectToMatter(matterResponse.matterId);
					}
				}
				break;
			}
		}

		this.isLoading$.next(false);
	}

	private HandleInvalidMatterType(matterType: string) {
		this.dialog.open(InvalidMatterTypeComponent, { data: matterType });
	}

	/**
	 * Checks if we have already showed a warning to the user previously.
	 * Checks the storage to determine if the current matter has been added to storage because it requires a
	 * warning before importing it from LEAP to Closer. If the matter is found, it will show the warning dialog.
	 */
	private async handleWarningBeforeImportUsingStorage() {
		const mattersWithWarningIds = GetMattersWithWarning();
		if (
			mattersWithWarningIds.some(
				(idFromStorage) => idFromStorage === this.matterId,
			)
		) {
			Logger.info(
				`Matter ${this.matterId} was created more than 24Hrs ago. Thus the user will need to decide if they want to import it into Closer.`,
			);
			await this.showWarningBeforeImport(this.dialog, this.matterId, true);
		}
	}

	private async handleWarningBeforeImport(
		matterImportedCheckResponse: LeapCheckMatterResponse,
	) {
		if (
			!matterImportedCheckResponse.matterImported &&
			matterImportedCheckResponse.warnBeforeImport
		) {
			Logger.info(
				`Matter ${this.matterId} was created more than 24Hrs ago. Thus the user will need to decide if they want to import it into Closer.`,
			);
			await this.showWarningBeforeImport(this.dialog, this.matterId, false);
		}
	}

	private currentMatterIsInStorage(): boolean {
		const mattersWithWarningIds = GetMattersWithWarning();
		const exists = mattersWithWarningIds.some(
			(idFromStorage) => idFromStorage === this.matterId,
		);

		return exists;
	}

	/**
	 * Shows the warning dialog based on the specified condition. It will only return once the user has
	 * agreed to import the matter.
	 * @param dialog An instance of MatDialog.
	 * @param matterId The ID of the matter for which the warning is being shown.
	 * @param showRejectedDialog A value of true means the user has already answered in a previous session so we will
	 * show the dialog to reflect that.
	 */
	private async showWarningBeforeImport(
		dialog: MatDialog,
		matterId: string,
		showRejectedDialog: boolean,
	): Promise<void> {
		if (showRejectedDialog) {
			if (this.currentMatterIsInStorage()) {
				const rejectImportDialog = dialog.open(RejectImportingMatterComponent);
				await firstValueFrom(rejectImportDialog.afterClosed());
			}
		}
		const warnBeforeImportDialog = dialog.open(
			WarnBeforeImportingMatterComponent,
			{
				disableClose: true,
			},
		);

		const dialogResult: {
			answer: YesOrNo;
		} = await firstValueFrom(warnBeforeImportDialog.afterClosed());

		const mattersWithWarningIds = GetMattersWithWarning();
		if (dialogResult.answer === YesOrNo.No) {
			// Add to storage, if not already added, to remember the user's answer for the future.
			if (!this.currentMatterIsInStorage()) {
				mattersWithWarningIds.push(matterId);
				SetMattersWithWarning(mattersWithWarningIds);
			}

			const rejectImportDialog = dialog.open(RejectImportingMatterComponent);
			await firstValueFrom(rejectImportDialog.afterClosed());
			await this.showWarningBeforeImport(dialog, matterId, false);
		} else {
			// Remove Matter ID from the storage because the user has agreed to import the matter from LEAP into Closer.
			if (this.currentMatterIsInStorage()) {
				mattersWithWarningIds.splice(mattersWithWarningIds.indexOf(matterId));
				SetMattersWithWarning(mattersWithWarningIds);
			}
		}
	}

	/**
	 * Redirects the user to the matter page with the specified ID.
	 * @param id The ID of the matter.
	 */
	private redirectToMatter(id) {
		this.navService.goToMatter(id);
	}

	private goToCreateMatter_APS(id) {
		this.navService.goToCreateMatter_APS(id);
	}

	unsubCustomTokenHandlerSub() {
		this.customTokenHandlerSub.unsubscribe();
	}
}
